import React, { useState } from 'react';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PencilSquareIcon,
  EyeIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { Lead, ValidationError } from './newRunTypes';
import { getPhoneFormatExamples } from './newRunValidation';
import { SUPPORTED_NUMBERS_DESCRIPTION } from '../../config/region';
import ErrorRowEditor from './ErrorRowEditor';
import ErrorEditorModal from './ErrorEditorModal';
import './NewRunLeadsUpload.css';

interface NewRunLeadsUploadProps {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  selectedFile: File | null;
  leads: Lead[];
  validationErrors: ValidationError[];
  previewMode: boolean;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onShowTemplate: () => void;
  onExportErrors: () => void;
  onTogglePreview: () => void;
  onClearFile: () => void;
  onSaveCorrections: (correctedLeads: Lead[]) => void;
}

const NewRunLeadsUpload: React.FC<NewRunLeadsUploadProps> = ({
  fileInputRef,
  selectedFile,
  leads,
  validationErrors,
  previewMode,
  onFileSelect,
  onDrop,
  onDragOver,
  onShowTemplate,
  onExportErrors,
  onTogglePreview,
  onClearFile,
  onSaveCorrections,
}) => {
  const formatExamples = getPhoneFormatExamples();
  const [showInlineEditor, setShowInlineEditor] = useState(false);
  const [showModalEditor, setShowModalEditor] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showAllLeadsPreview, setShowAllLeadsPreview] = useState(false);

  const errorCount = validationErrors.length;
  const canEditInline = errorCount > 0 && errorCount <= 10;
  const canEditInModal = errorCount > 10 && errorCount <= 50;
  const mustReupload = errorCount > 50;

  const handleRemoveClick = () => {
    if (leads.length > 0) {
      setShowRemoveConfirm(true);
    } else {
      onClearFile();
    }
  };

  const handleConfirmRemove = () => {
    onClearFile();
    setShowRemoveConfirm(false);
  };
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Upload Leads (CSV) *
        </label>
        <div
          className="border-2 border-dashed border-white/20 rounded-lg p-3 sm:p-4 lg:p-6 text-center hover:border-cyan-400/50 transition-colors bg-white/5"
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <CloudArrowUpIcon className="mx-auto h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-gray-300" />
          <div className="mt-2 sm:mt-3 lg:mt-4">
            <p className="text-xs sm:text-sm text-gray-300">
              Drag and drop your CSV file here, or{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-cyan-400 hover:text-cyan-300 font-medium"
              >
                browse
              </button>
            </p>
            <p className="text-xs text-gray-400 mt-1 hidden sm:block">
              Supported phone formats: {formatExamples.join(', ')}
            </p>
            <p className="text-xs text-gray-400 mt-1 sm:hidden">
              {SUPPORTED_NUMBERS_DESCRIPTION}
            </p>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={onShowTemplate}
                className="flex items-center justify-center text-xs font-medium w-full sm:w-auto"
              >
                <ArrowDownTrayIcon className="h-3 w-3 mr-1 text-cyan-400" />
                <span className="text-cyan-400 hover:text-cyan-300">
                  Download Template
                </span>
                <span className="text-gray-400 ml-1 hidden sm:inline">
                  (Get sample CSV format)
                </span>
              </button>
            </div>
          </div>
          <input
            ref={fileInputRef as React.RefObject<HTMLInputElement>}
            type="file"
            accept=".csv"
            onChange={onFileSelect}
            className="hidden"
          />
        </div>
        {selectedFile && (
          <div className="mt-3 flex items-center justify-between text-sm text-gray-300 bg-slate-800/50 border border-white/20 rounded-lg px-3 py-2">
            <div className="flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              <span>{selectedFile.name} ({leads.length} leads)</span>
            </div>
            <button
              type="button"
              onClick={handleRemoveClick}
              className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
              title="Remove file"
            >
              <XMarkIcon className="h-4 w-4" />
              Remove
            </button>
          </div>
        )}
        {validationErrors.length > 0 && !showInlineEditor && (
          <div className="mt-4 bg-slate-800/50 border-l-4 border-l-red-500 border border-white/10 rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                <h4 className="text-sm font-medium text-white">
                  CSV Validation Errors ({validationErrors.length} {validationErrors.length === 1 ? 'error' : 'errors'})
                </h4>
              </div>
              <div className="flex gap-2">
                {canEditInline && (
                  <button
                    type="button"
                    onClick={() => setShowInlineEditor(true)}
                    className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-md hover:from-cyan-600 hover:to-blue-700 transition-colors"
                  >
                    <PencilSquareIcon className="h-3 w-3 mr-1" />
                    Fix Errors
                  </button>
                )}
                {canEditInModal && (
                  <button
                    type="button"
                    onClick={() => setShowModalEditor(true)}
                    className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-md hover:from-cyan-600 hover:to-blue-700 transition-colors"
                  >
                    <PencilSquareIcon className="h-3 w-3 mr-1" />
                    Open Editor
                  </button>
                )}
                <button
                  type="button"
                  onClick={onExportErrors}
                  className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-300 bg-white/5 border border-white/20 rounded-md hover:bg-white/10 hover:text-white transition-colors"
                  title="Export validation errors as CSV"
                >
                  <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                  Export Errors
                </button>
              </div>
            </div>
            <div className="max-h-40 overflow-y-auto scrollbar-hide">
              <div className="space-y-2">
                {validationErrors.slice(0, 10).map((error, index) => (
                  <div
                    key={index}
                    className="text-sm text-gray-300 bg-slate-900/50 border border-red-500/30 rounded px-3 py-2"
                  >
                    <span className="font-medium text-red-400">Row {error.row}:</span>{' '}
                    {error.field === 'general'
                      ? error.message
                      : `${error.field} - ${error.message}`}
                  </div>
                ))}
                {validationErrors.length > 10 && (
                  <div className="text-xs text-gray-400 text-center py-2">
                    ... and {validationErrors.length - 10} more errors
                  </div>
                )}
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-400">
              {canEditInline && 'Click "Fix Errors" to correct them directly, or '}
              {canEditInModal && 'Click "Open Editor" to fix errors in a larger view, or '}
              {mustReupload && 'Too many errors. '}
              Please fix these errors and {mustReupload ? 're-upload' : 'validate again'}.
            </p>
          </div>
        )}

        {showInlineEditor && canEditInline && (
          <ErrorRowEditor
            leads={leads}
            validationErrors={validationErrors}
            onSaveCorrections={(corrected) => {
              onSaveCorrections(corrected);
              // Don't close editor - let user see feedback and continue fixing errors
            }}
            onCancel={() => setShowInlineEditor(false)}
          />
        )}

        {showModalEditor && canEditInModal && (
          <ErrorEditorModal
            leads={leads}
            validationErrors={validationErrors}
            onSaveCorrections={(corrected) => {
              onSaveCorrections(corrected);
              // Don't close modal - let user see feedback and continue fixing errors
            }}
            onClose={() => setShowModalEditor(false)}
          />
        )}
      </div>
      {leads.length > 0 && (
        <div className="bg-gradient-to-br from-slate-800/20 to-slate-900/30 backdrop-blur-md rounded-xl shadow-lg border border-slate-700/30 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-700/20 to-slate-800/30 px-3 py-2 sm:px-4 sm:py-3 border-b border-slate-700/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <div className="p-1 sm:p-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg flex-shrink-0">
                  <DocumentTextIcon className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm sm:text-base font-bold text-white truncate">
                    Leads Preview
                  </h3>
                  <p className="text-xs text-gray-300 truncate">
                    {selectedFile?.name} ({leads.length} leads)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onTogglePreview}
                className="px-2 py-1 sm:px-3 sm:py-1.5 bg-white/10 text-white text-xs font-medium rounded-md hover:bg-white/20 transition-colors border border-white/20 flex-shrink-0 ml-2"
              >
                {previewMode ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div className="px-4 py-2">
            <div className="flex items-center space-x-2 p-2 bg-green-500/10 border border-green-500/20 rounded-md">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
              <p className="text-xs text-green-300 font-medium">
                Successfully parsed {leads.length} leads
              </p>
            </div>
          </div>
          {previewMode && (
            <div className="px-3 pb-3 sm:px-4 sm:pb-4">
              <div className="bg-gradient-to-br from-slate-800/20 to-slate-900/30 rounded-lg border border-slate-700/30 overflow-hidden">
                <div className="bg-gradient-to-r from-slate-700/20 to-slate-800/30 px-2 py-1.5 sm:px-3 sm:py-2 border-b border-slate-700/30">
                  <h4 className="text-xs font-semibold text-white">Lead Data</h4>
                </div>
                <div className="max-h-32 sm:max-h-40 overflow-y-auto scrollbar-hide">
                  <div className="overflow-x-auto scrollbar-hide">
                    <table className="w-full text-xs min-w-[300px]">
                      <thead>
                        <tr className="border-b border-slate-700/30">
                          <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 text-xs font-semibold text-slate-300 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 text-xs font-semibold text-slate-300 uppercase tracking-wider">
                            Phone
                          </th>
                          <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 text-xs font-semibold text-slate-300 uppercase tracking-wider hidden sm:table-cell">
                            Email
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700/20">
                        {leads.slice(0, showAllLeadsPreview ? leads.length : 5).map((lead, index) => (
                          <tr
                            key={index}
                            className="hover:bg-slate-800/30 transition-colors"
                          >
                            <td className="py-1.5 sm:py-2 px-2 sm:px-3 text-white font-medium truncate max-w-[120px] sm:max-w-none">
                              {lead.name}
                            </td>
                            <td className="py-1.5 sm:py-2 px-2 sm:px-3 text-slate-300 text-xs truncate max-w-[100px] sm:max-w-none">
                              {lead.phone}
                            </td>
                            <td className="py-1.5 sm:py-2 px-2 sm:px-3 text-slate-300 hidden sm:table-cell truncate">
                              {lead.email || '-'}
                            </td>
                          </tr>
                        ))}
                        {leads.length > 5 && (
                          <tr>
                            <td colSpan={3} className="py-1.5 sm:py-2 px-2 sm:px-3 text-center">
                              {showAllLeadsPreview ? (
                                <button
                                  type="button"
                                  onClick={() => setShowAllLeadsPreview(false)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                                >
                                  <ChevronUpIcon className="h-3.5 w-3.5" />
                                  Show less
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setShowAllLeadsPreview(true)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                                >
                                  <EyeIcon className="h-3.5 w-3.5" />
                                  View all {leads.length} leads
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Remove Confirmation Dialog */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div 
            className="bg-slate-900 border border-white/20 rounded-lg shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-3">Remove uploaded file?</h3>
            <p className="text-sm text-gray-300 mb-6">
              This will remove your uploaded CSV and any corrections you've made. You'll need to upload a new file.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowRemoveConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-white/5 border border-white/20 rounded hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
              >
                Remove File
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewRunLeadsUpload;
